import queryString from "query-string";

import type { Network } from "../../interfaces/network.types";
import type { NetworkPayload, Payload } from "./request.types";
import type { EndpointOptions } from "./schema.request.types";
import type { ApiMethod, ApiPath } from "./schema.types";

const API_VERSION_PREFIX = "/v1/";

const PATH_PARAMETER = /\{([^}]+)\}/g;

type EndpointPathParams = Record<string, string | number | boolean>;

type EndpointQueryParams = Record<
  string,
  | string
  | number
  | boolean
  | null
  | undefined
  | ReadonlyArray<string | number | boolean>
>;

export type EndpointArgs = {
  params?: {
    path?: EndpointPathParams;
    query?: EndpointQueryParams;
  };
  body?: unknown;
  options?: EndpointOptions;
};

/** Drops the `/v1/` prefix the transports re-add. */
export const toRelativeUrl = (path: string): string => {
  if (!path.startsWith(API_VERSION_PREFIX)) {
    throw new Error(
      `Expected schema path "${path}" to start with "${API_VERSION_PREFIX}".`,
    );
  }
  return path.slice(API_VERSION_PREFIX.length);
};

/** Substitutes `{uuid}`-style path parameters. */
export const interpolatePath = (
  template: string,
  pathParams: EndpointPathParams = {},
): string =>
  template.replace(PATH_PARAMETER, (_match, name: string) => {
    const value = pathParams[name];
    if (value === undefined || value === null || value === "") {
      throw new Error(
        `Missing path parameter "${name}" for endpoint "${template}".`,
      );
    }
    return encodeURIComponent(String(value));
  });

export const toQueryString = (query: EndpointQueryParams = {}): string => {
  const search = queryString.stringify(query, { skipNull: true });
  return search ? `?${search}` : "";
};

const buildUrl = (path: ApiPath, params?: EndpointArgs["params"]): string =>
  `${interpolatePath(toRelativeUrl(path), params?.path)}${toQueryString(
    params?.query,
  )}`;

/**
 * Builds a `NetworkPayload` for use with `useTypedRequestWithFeedback`
 *
 * Every public API path is prefixed `/v1/` and both transports resolve their
 * base URL to `.../v1`, so a schema path becomes a byzantine `url` by
 * dropping that prefix.
 */
export const endpoint = (
  method: ApiMethod,
  path: ApiPath,
  { params, body, options }: EndpointArgs = {},
): NetworkPayload => ({
  url: buildUrl(path, params),
  options: {
    ...options,
    method: method.toUpperCase() as Network.Method,
    ...(body === undefined ? {} : { payload: body }),
  } as Network.Options<Payload>,
});
